import { useState } from "react";
import { Link } from "@inertiajs/react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { wholesale_forecast_by_account_path } from "@/lib/routes";
import { useMarket } from "@/lib/useMarket";
import AddCustomerRow from "./AddCustomerRow";
import ForecastCell from "./ForecastCell";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/Tooltip";
import { PRODUCT_COL_WIDTH } from "@/lib/gridLayout";
import {
  availableCustomersForProduct,
  formatUnits,
  isLocked,
  productLabel,
  productSubtotal,
  type CustomerInfo,
  type ForecastProduct,
} from "../lib/forecastGrid";

interface Props {
  product: ForecastProduct;
  months: string[];
  lockedThrough: string;
  canEdit: boolean;
  currentUserId: string | null;
  customers: CustomerInfo[];
  onCellSave: (customerId: string, productExternalId: string, month: string, units: string) => void;
  onAddCustomer: (productExternalId: string, customerId: string) => void;
}

// Faint primary tint marks group header bands, matching the Bottom Up grid.
const GROUP_BG = "color-mix(in srgb, var(--primary) 5%, var(--background))";

export default function ProductGroup({
  product,
  months,
  lockedThrough,
  canEdit,
  currentUserId,
  customers,
  onCellSave,
  onAddCustomer,
}: Props) {
  const market = useMarket();
  const [collapsed, setCollapsed] = useState(false);

  return (
    <>
      <tr
        style={{ borderColor: "var(--accent)", background: GROUP_BG }}
        className="cursor-pointer select-none border-b"
        onClick={() => setCollapsed((c) => !c)}
      >
        <td
          style={{ background: GROUP_BG, borderColor: "var(--border)", width: PRODUCT_COL_WIDTH, maxWidth: PRODUCT_COL_WIDTH }}
          className="sticky left-0 z-10 border-r px-3 py-1.5 overflow-hidden"
        >
          <div
            className="flex items-center gap-1.5 whitespace-nowrap"
            title={`${product.name} · #${product.external_id}`}
          >
            {collapsed ? (
              <ChevronRight size={14} className="shrink-0 text-muted-foreground" />
            ) : (
              <ChevronDown size={14} className="shrink-0 text-muted-foreground" />
            )}
            {/* stopPropagation: the row itself toggles collapse */}
            <Link
              href={wholesale_forecast_by_account_path(market.slug, { product_id: product.external_id })}
              onClick={(e) => e.stopPropagation()}
              style={{ color: "var(--foreground)" }}
              className="truncate font-semibold hover:underline"
            >
              {productLabel(product)}
            </Link>
            <span className="shrink-0 text-[10px] text-muted-foreground">
              {product.rows.length} {product.rows.length === 1 ? "cust." : "custs."}
            </span>
          </div>
        </td>
        {months.map((month) => (
          <td
            key={month}
            style={{ color: "var(--foreground)" }}
            className="px-2.5 py-1.5 text-right text-[11px] font-semibold tabular-nums"
          >
            {formatUnits(productSubtotal(product, month))}
          </td>
        ))}
      </tr>

      {!collapsed &&
        product.rows.map((row) => {
          const mine = currentUserId != null && row.pic_user?.id === currentUserId;
          return (
            <tr key={row.customer.id} style={{ borderColor: "var(--accent)" }} className="border-b">
              <td
                style={{ background: "var(--background)", borderColor: "var(--border)", width: PRODUCT_COL_WIDTH, maxWidth: PRODUCT_COL_WIDTH }}
                className="sticky left-0 z-10 border-r px-3 py-1 pl-8 overflow-hidden"
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex w-full items-center gap-2 whitespace-nowrap">
                      <span style={{ color: "var(--foreground)" }} className="truncate">
                        {row.customer.name}
                      </span>
                      {row.pic_user ? (
                        mine ? (
                          <Badge variant="info">you</Badge>
                        ) : (
                          <span className="shrink-0 text-[10px] text-muted-foreground">{row.pic_user.name}</span>
                        )
                      ) : (
                        <span className="shrink-0 text-[10px] text-muted-foreground">—</span>
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>{row.customer.name}</TooltipContent>
                </Tooltip>
              </td>
              {months.map((month) => {
                const locked = isLocked(month, lockedThrough);
                return (
                  <td key={month} className="px-1 py-0.5">
                    <ForecastCell
                      value={row.values[month]}
                      editable={canEdit && !locked}
                      muted={locked}
                      onSave={(units) => onCellSave(row.customer.id, product.external_id, month, units)}
                    />
                  </td>
                );
              })}
            </tr>
          );
        })}

      {!collapsed && canEdit && (
        <AddCustomerRow
          productName={product.name}
          monthsCount={months.length}
          availableCustomers={availableCustomersForProduct(product, customers)}
          currentUserId={currentUserId}
          onAdd={(customerId) => onAddCustomer(product.external_id, customerId)}
        />
      )}
    </>
  );
}
